import moment from 'moment';

// Define a custom TranslationFunction type
type TranslationFunction = (
  key: string | string[],
  options?: Record<string, any>
) => string;

export const generateRRule = (
  frequencyValue: string,
  startDate: Date,
  stopEventDate: Date | null,
  selectedOption: number | null,
  groupTimezone: string,
  weekNumberInMonth: number,
  t: TranslationFunction
): string | null => {
  const stopDateFormatted =
    stopEventDate && moment(stopEventDate).isValid()
      ? moment(stopEventDate).tz(groupTimezone, true).format('YYYYMMDDTHHmmss')
      : null;

  switch (frequencyValue) {
    case 'daily':
      return stopDateFormatted && selectedOption === 1
        ? `RRULE:FREQ=DAILY;UNTIL=${stopDateFormatted}`
        : 'RRULE:FREQ=DAILY';

    case 'weekly':
      return stopDateFormatted && selectedOption === 1
        ? `RRULE:FREQ=WEEKLY;BYDAY=${moment(startDate)
            .tz(groupTimezone)
            .format('dd')
            .toUpperCase()};UNTIL=${stopDateFormatted}`
        : `RRULE:FREQ=WEEKLY;BYDAY=${moment(startDate)
            .tz(groupTimezone)
            .format('dd')
            .toUpperCase()}`;

    case 'every_interval_week_on':
      return stopDateFormatted && selectedOption === 1
        ? `RRULE:FREQ=WEEKLY;BYDAY=${moment(startDate)
            .tz(groupTimezone)
            .format('dd')
            .toUpperCase()};INTERVAL=2;UNTIL=${stopDateFormatted}`
        : `RRULE:FREQ=WEEKLY;BYDAY=${moment(startDate)
            .tz(groupTimezone)
            .format('dd')
            .toUpperCase()};INTERVAL=2`;

    case 'monthly_on_selected_day':
      // Instead of using ordinal, use the raw week number
      const weekNumber = weekNumberInMonth.toString(); // Raw week number, no ordinal

      // Format the weekday correctly (e.g., "Fr" for Friday)
      const dayOfWeek = moment(startDate)
        .tz(groupTimezone)
        .format('dd')
        .toUpperCase(); // This gives you "FR" for Friday

      // For "Monthly on the 4th Friday", you should only care about the 4th week
      if (weekNumberInMonth === 4) {
        return stopDateFormatted && selectedOption === 1
          ? `RRULE:FREQ=MONTHLY;BYDAY=+4${dayOfWeek};UNTIL=${stopDateFormatted}`
          : `RRULE:FREQ=MONTHLY;BYDAY=+4${dayOfWeek}`;
      }

      // Other logic for different week numbers if applicable
      return stopDateFormatted && selectedOption === 1
        ? `RRULE:FREQ=MONTHLY;BYDAY=+${weekNumber}${dayOfWeek};UNTIL=${stopDateFormatted}`
        : `RRULE:FREQ=MONTHLY;BYDAY=+${weekNumber}${dayOfWeek}`;
    case 'annually_on_month_day':
      return stopDateFormatted && selectedOption === 1
        ? `RRULE:FREQ=YEARLY;BYMONTH=${moment(startDate)
            .tz(groupTimezone)
            .format(
              'M'
            )};BYMONTHDAY=${startDate.getDate()};UNTIL=${stopDateFormatted}`
        : `RRULE:FREQ=YEARLY;BYMONTH=${moment(startDate)
            .tz(groupTimezone)
            .format('M')};BYMONTHDAY=${startDate.getDate()}`;

    default:
      return ''; // No frequency selected
  }
};

export const formatDateForRules = (
  startDate: Date,
  startTime: string
): string => {
  // Validate startTime to make sure it's in the correct "HHmm" format
  if (!startTime || startTime.length !== 4 || isNaN(Number(startTime))) {
    // Handle invalid startTime by defaulting to "0000" (midnight)
    startTime = '0000';
  }

  // Extract the components of the startTime (assuming it's in "HHmm" format)
  const hours = parseInt(startTime.slice(0, 2), 10);
  const minutes = parseInt(startTime.slice(2, 4), 10);

  // Set the time based on the provided startTime
  startDate.setHours(hours);
  startDate.setMinutes(minutes);
  startDate.setSeconds(0); // Set seconds to 0 for accuracy

  // Get the components of the date
  const year = startDate.getFullYear();
  const month = String(startDate.getMonth() + 1).padStart(2, '0'); // Adding 1 to month as getMonth() is 0-indexed
  const day = String(startDate.getDate()).padStart(2, '0');
  const hour = String(startDate.getHours()).padStart(2, '0');
  const minute = String(startDate.getMinutes()).padStart(2, '0');
  const second = String(startDate.getSeconds()).padStart(2, '0');

  // Return the formatted date
  return `${year}${month}${day}T${hour}${minute}${second}`;
};
